import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api/client";
import { IndicatorDto, IndicatorPeriodDto, CreateIndicatorValueDto, UpdateIndicatorValueDto, IndicatorValueDto, IndicatorFrequencyDto, DepartmentDto, ApiResponse } from "@/lib/types/api";
import { handleApiError } from "@/lib/utils/errors";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowLeft, FileText, AlertCircle, Send, Calendar, CalendarDays, CalendarRange, CalendarClock, Search, MessageSquare, X, Filter, Building2, MoreVertical, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-hot-toast";
import PeriodSelector from "./period-selector";

type FrequencyTab = "all" | "yearly" | "quarterly" | "monthly" | "weekly";

interface IndicatorDataEntryProps {
  locationId: string;
  locationType: "block" | "district";
  locationName: string;
}

export default function IndicatorDataEntry({
  locationId,
  locationType,
  locationName,
}: IndicatorDataEntryProps) {
  const [indicators, setIndicators] = useState<IndicatorDto[]>([]);
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [frequencies, setFrequencies] = useState<IndicatorFrequencyDto[]>([]);
  const [selectedIndicator, setSelectedIndicator] = useState<IndicatorDto | null>(null);
  const [periods, setPeriods] = useState<IndicatorPeriodDto[]>([]);
  const [selectedTab, setSelectedTab] = useState<FrequencyTab>("all");
  const [selectedDepartment, setSelectedDepartment] = useState<string>("all");
  const [selectedProgramType, setSelectedProgramType] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [existingValue, setExistingValue] = useState<IndicatorValueDto | null>(null);
  const [showRemarks, setShowRemarks] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [indicatorPeriods, setIndicatorPeriods] = useState<Record<string, IndicatorPeriodDto[]>>({});
  const [existingValues, setExistingValues] = useState<IndicatorValueDto[]>([]);
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<CreateIndicatorValueDto>({
    indicatorId: "",
    periodId: "",
    districtId: locationType === "district" ? locationId : undefined,
    blockId: locationType === "block" ? locationId : undefined,
    value: 0,
    numeratorValue: undefined,
    denominatorValue: undefined,
    remarks: "",
  });

  useEffect(() => {
    loadData();
  }, [locationId, locationType]);

  // Check for existing data when indicator and period are selected
  useEffect(() => {
    if (formData.indicatorId && formData.periodId) {
      checkExistingData();
    }
  }, [formData.indicatorId, formData.periodId]);

  const checkExistingData = async () => {
    try {
      // Fetch all values for this location
      const valuesRes = locationType === "block"
        ? await apiClient.getIndicatorValuesByBlockId(locationId, 1, 10000) // Large pageSize to get all values
        : await apiClient.getIndicatorValuesByDistrictId(locationId);

      if (valuesRes.success && valuesRes.data) {
        // Find existing value for selected indicator and period
        const values = 'items' in valuesRes.data ? valuesRes.data.items : valuesRes.data;
        const existing = values.find(
          (v: { indicatorId: string; periodId: string }) => v.indicatorId === formData.indicatorId && v.periodId === formData.periodId
        );

        if (existing) {
          setExistingValue(existing);
          // Populate form with existing data
          setFormData(prev => ({
            ...prev,
            value: existing.value,
            numeratorValue: existing.numeratorValue ?? undefined,
            denominatorValue: existing.denominatorValue ?? undefined,
            remarks: existing.remarks ?? "",
          }));
          // Show remarks section if existing value has remarks
          if (existing.remarks) {
            setShowRemarks(true);
          }
        } else {
          setExistingValue(null);
          // Reset form values to defaults when no existing data
          setFormData(prev => ({
            ...prev,
            value: 0,
            numeratorValue: undefined,
            denominatorValue: undefined,
            remarks: "",
          }));
        }
      }
    } catch (err: unknown) {
      if (process.env.NODE_ENV === 'development') {
        console.error(handleApiError(err, "Failed to check existing data"));
      }
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [indicatorsRes, frequenciesRes, departmentsRes, valuesRes] = await Promise.all([
        locationType === "block" 
          ? apiClient.getBlockLevelIndicators()
          : apiClient.getDistrictLevelIndicators(),
        apiClient.getIndicatorFrequencies(),
        apiClient.getDepartments(),
        locationType === "block"
          ? apiClient.getIndicatorValuesByBlockId(locationId, 1, 10000)
          : apiClient.getIndicatorValuesByDistrictId(locationId),
      ]);

      if (indicatorsRes.success && indicatorsRes.data) {
        setIndicators(indicatorsRes.data);
        
        // Fetch periods for each indicator
        const periodsMap: Record<string, IndicatorPeriodDto[]> = {};
        const uniqueFrequencies = [...new Set(indicatorsRes.data.map(ind => ind.frequencyId))];
        
        await Promise.all(
          uniqueFrequencies.map(async (frequencyId) => {
            const periodsRes = await apiClient.getAvailablePeriods(frequencyId);
            if (periodsRes.success && periodsRes.data && indicatorsRes.data) {
              // Store periods for all indicators with this frequency
              indicatorsRes.data
                .filter(ind => ind.frequencyId === frequencyId)
                .forEach(ind => {
                  periodsMap[ind.id] = periodsRes.data!;
                });
            }
          })
        );
        
        setIndicatorPeriods(periodsMap);
      }
      if (frequenciesRes.success && frequenciesRes.data) {
        setFrequencies(frequenciesRes.data);
      }
      if (departmentsRes.success && departmentsRes.data) {
        setDepartments(departmentsRes.data);
      }
      if (valuesRes.success && valuesRes.data) {
        const values = 'items' in valuesRes.data ? valuesRes.data.items : valuesRes.data;
        setExistingValues(values);
      }
    } catch (err: unknown) {
      setError(handleApiError(err, "Failed to load indicators"));
    } finally {
      setIsLoading(false);
    }
  };

  // Filter indicators based on selected tab and search query
  const filteredIndicators = useMemo(() => {
    const frequencyMap = frequencies.reduce((acc, freq) => {
      acc[freq.id] = freq.frequencyName.toLowerCase();
      return acc;
    }, {} as Record<string, string>);

    let filtered = indicators;

    // Filter by department
    if (selectedDepartment !== "all") {
      filtered = filtered.filter((indicator) => indicator.departmentId === selectedDepartment);
    }

    // Filter by program type
    if (selectedProgramType !== "all") {
      filtered = filtered.filter((indicator) => indicator.programType === selectedProgramType);
    }

    // Filter by frequency tab
    if (selectedTab !== "all") {
      filtered = filtered.filter((indicator) => {
        const frequencyName = frequencyMap[indicator.frequencyId];
        return frequencyName === selectedTab;
      });
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((indicator) => 
        indicator.indicatorName.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [indicators, selectedTab, selectedDepartment, selectedProgramType, frequencies, searchQuery]);

  // Get frequency icon and label
  const getFrequencyInfo = (tab: FrequencyTab) => {
    switch (tab) {
      case "yearly":
        return { icon: Calendar, label: "Yearly", color: "text-blue-600" };
      case "quarterly":
        return { icon: CalendarRange, label: "Quarterly", color: "text-green-600" };
      case "monthly":
        return { icon: CalendarDays, label: "Monthly", color: "text-purple-600" };
      case "weekly":
        return { icon: CalendarClock, label: "Weekly", color: "text-orange-600" };
      default:
        return { icon: FileText, label: "All", color: "text-gray-600" };
    }
  };

  const handleIndicatorSelect = async (indicator: IndicatorDto) => {
    setSelectedIndicator(indicator);
    setExistingValue(null);
    
    // Reset form first
    setFormData({
      indicatorId: indicator.id,
      periodId: "",
      districtId: locationType === "district" ? locationId : undefined,
      blockId: locationType === "block" ? locationId : undefined,
      value: 0,
      numeratorValue: undefined,
      denominatorValue: undefined,
      remarks: "",
    });

    // Load periods for this indicator's frequency
    try {
      const periodsRes = await apiClient.getAvailablePeriods(indicator.frequencyId);
      if (periodsRes.success && periodsRes.data && periodsRes.data.length > 0) {
        setPeriods(periodsRes.data);
        
        // Auto-select the period closest to today's date
        const today = new Date();
        let closestPeriod = periodsRes.data[0];
        
        // First, try to find a period that contains today
        const currentPeriod = periodsRes.data.find(period => {
          const periodStart = new Date(period.periodStartDate);
          const periodEnd = new Date(period.periodEndDate);
          return today >= periodStart && today <= periodEnd;
        });
        
        if (currentPeriod) {
          closestPeriod = currentPeriod;
        } else {
          // Find the period with the closest date to today
          closestPeriod = periodsRes.data.reduce((closest, period) => {
            const periodStart = new Date(period.periodStartDate);
            const periodEnd = new Date(period.periodEndDate);
            const closestStart = new Date(closest.periodStartDate);
            const closestEnd = new Date(closest.periodEndDate);
            
            const currentDiff = Math.min(
              Math.abs(today.getTime() - periodStart.getTime()),
              Math.abs(today.getTime() - periodEnd.getTime())
            );
            
            const closestDiff = Math.min(
              Math.abs(today.getTime() - closestStart.getTime()),
              Math.abs(today.getTime() - closestEnd.getTime())
            );
            
            return currentDiff < closestDiff ? period : closest;
          }, periodsRes.data[0]);
        }
        
        // Set the period in form data
        setFormData(prev => ({ 
          ...prev, 
          periodId: closestPeriod.id 
        }));
      }
    } catch (err: unknown) {
      toast.error(handleApiError(err, "Failed to load periods"));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.indicatorId || !formData.periodId) {
      toast.error("Please select an indicator and period");
      return;
    }

    setIsSubmitting(true);
    try {
      let response: ApiResponse<IndicatorValueDto>;
      
      if (existingValue) {
        // Update existing value
        const updateData: UpdateIndicatorValueDto = {
          value: formData.value,
          numeratorValue: formData.numeratorValue,
          denominatorValue: formData.denominatorValue,
          remarks: formData.remarks,
        };
        response = await apiClient.updateIndicatorValue(existingValue.id, updateData);
        if (response.success) {
          toast.success("Indicator value updated successfully!");
        }
      } else {
        // Create new value
        response = await apiClient.createIndicatorValue(formData);
        if (response.success && response.data) {
          toast.success("Indicator value submitted successfully!");
          // Update existingValues to reflect the newly created value
          setExistingValues(prev => [...prev, response.data!]);
        }
      }
      
      if (response.success) {
        // Reset form
        setSelectedIndicator(null);
        setExistingValue(null);
        setFormData({
          indicatorId: "",
          periodId: "",
          districtId: locationType === "district" ? locationId : undefined,
          blockId: locationType === "block" ? locationId : undefined,
          value: 0,
          numeratorValue: undefined,
          denominatorValue: undefined,
          remarks: "",
        });
        setPeriods([]);
      } else {
        toast.error(response.message || "Failed to submit indicator value");
      }
    } catch (err: unknown) {
      toast.error(handleApiError(err, "Failed to submit indicator value"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard/indicator-data');
  };

  if (isLoading) {
    return <div className="flex justify-center py-8">Loading...</div>;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Button variant="outline" size="sm" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Card className="border-red-200 dark:border-red-800 bg-red-50/20 dark:bg-red-950/20">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <AlertCircle className="mx-auto h-12 w-12 text-red-600 dark:text-red-400 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Error</h3>
              <p className="text-gray-600 dark:text-gray-400">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 rounded">
              <FileText className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                Add Indicator Data
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                <span className="font-medium text-gray-700 dark:text-gray-300">{locationName}</span>
              </p>
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleBack}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
          Back
        </Button>
      </div>

      {/* Filters Section */}
      <div className="space-y-2 py-3 px-4 bg-gray-50 dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700">
          {/* Department and Program Type Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {/* Department Filter */}
          <Select
            value={selectedDepartment}
            onValueChange={(value) => {
              setSelectedDepartment(value);
              setSelectedIndicator(null);
              setPeriods([]);
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>
                  {dept.departmentName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Program Type Filter */}
          <Select
            value={selectedProgramType}
            onValueChange={(value) => {
              setSelectedProgramType(value);
              setSelectedIndicator(null);
              setPeriods([]);
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="All Programs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Programs</SelectItem>
              <SelectItem value="Aspirational District Program">Aspirational District Program</SelectItem>
              <SelectItem value="Aspirational Block Program">Aspirational Block Program</SelectItem>
            </SelectContent>
          </Select>
          </div>

          {/* Frequency Tabs Row */}
          <div className="flex items-center gap-1 overflow-x-auto">
            <button
              type="button"
              onClick={() => {
                setSelectedTab("all");
                setSelectedIndicator(null);
                setPeriods([]);
              }}
              className={`px-2 py-1 text-xs font-medium rounded transition-all whitespace-nowrap ${
                selectedTab === "all"
                  ? "bg-primary text-primary-foreground shadow-sm border-2 border-primary dark:border-primary colorful:border-[#057dbb] yellow:border-[#fbb615] pink:border-[#df1b82] orange:border-[#f36f26] blue:border-[#057dbb] red:border-[#c22034] green:border-[#3ead49]"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 border border-transparent"
              }`}
            >
              All
            </button>
            {frequencies.map((freq) => (
              <button
                key={freq.id}
                type="button"
                onClick={() => {
                  setSelectedTab(freq.frequencyName.toLowerCase() as FrequencyTab);
                  setSelectedIndicator(null);
                  setPeriods([]);
                }}
                className={`px-2 py-1 text-xs font-medium rounded transition-all whitespace-nowrap ${
                  selectedTab === freq.frequencyName.toLowerCase()
                    ? "bg-primary text-primary-foreground shadow-sm border-2 border-primary dark:border-primary colorful:border-[#057dbb] yellow:border-[#fbb615] pink:border-[#df1b82] orange:border-[#f36f26] blue:border-[#057dbb] red:border-[#c22034] green:border-[#3ead49]"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 border border-transparent"
                }`}
              >
                {freq.frequencyName}
              </button>
            ))}
          </div>

            {/* Active Filters and Clear All */}
            {(selectedDepartment !== "all" || selectedProgramType !== "all" || selectedTab !== "all") && (
              <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t">
                <span className="text-xs font-medium text-gray-500">Active:</span>
                {selectedDepartment !== "all" && (
                  <span className="inline-flex items-center px-1.5 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                    {departments.find((d) => d.id === selectedDepartment)?.departmentName || selectedDepartment}
                  </span>
                )}
                {selectedProgramType !== "all" && (
                  <span className="inline-flex items-center px-1.5 py-0.5 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded">
                    {selectedProgramType === "Aspirational District Program" ? "District" : "Block"}
                  </span>
                )}
                {selectedTab !== "all" && (
                  <span className="inline-flex items-center px-1.5 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded capitalize">
                    {selectedTab}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedDepartment("all");
                    setSelectedProgramType("all");
                    setSelectedTab("all");
                    setSelectedIndicator(null);
                    setPeriods([]);
                  }}
                  className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20">
                  Clear
                </Button>
              </div>
            )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Indicators List */}
        <Card className="lg:col-span-1 border-2">
          <CardHeader className="py-3 border-b bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-primary" />
              <div>
                <CardTitle className="text-sm font-semibold">Select Indicator</CardTitle>
                <CardDescription className="text-xs mt-0">
                  {locationType === 'district' ? 'District' : 'Block'} level • {filteredIndicators.length} available
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3">
            {/* Search Box */}
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search indicators..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8 text-xs"
                />
              </div>
            </div>
            {filteredIndicators.length === 0 ? (
              <div className="text-center py-8 text-gray-600 dark:text-gray-500">
                <FileText className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 colorful:text-gray-500 yellow:text-[#fbb615]/50 pink:text-[#df1b82]/50 orange:text-[#f36f26]/50 blue:text-[#057dbb]/50 red:text-[#c22034]/50 green:text-[#3ead49]/50/50/50/50/50/50/50 mb-3" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedTab === "all" 
                    ? `No ${locationType}-level indicators available`
                    : `No ${selectedTab} indicators available`
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                {filteredIndicators.map((indicator, index) => {
                  const frequency = frequencies.find((f) => f.id === indicator.frequencyId);
                  const department = departments.find((d) => d.id === indicator.departmentId);
                  const frequencyName = frequency?.frequencyName.toLowerCase() || "";
                  
                  // Determine color based on frequency type
                  let frequencyColor = "bg-gray-400 dark:bg-gray-700 text-gray-900 dark:text-gray-300";
                  if (frequencyName.includes("yearly")) {
                    frequencyColor = "bg-blue-600 text-white";
                  } else if (frequencyName.includes("quarterly")) {
                    frequencyColor = "bg-green-600 text-white";
                  } else if (frequencyName.includes("monthly")) {
                    frequencyColor = "bg-purple-600 text-white";
                  } else if (frequencyName.includes("weekly")) {
                    frequencyColor = "bg-orange-600 text-white";
                  }
                  
                  const indicatorPeriodsData = indicatorPeriods[indicator.id] || [];
                  
                  return (
                    <div
                      key={indicator.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedIndicator?.id === indicator.id
                          ? "border-primary bg-primary/20 shadow-sm"
                          : "border-gray-200 dark:border-gray-700 bg-gray-100/50 dark:bg-gray-800/50 hover:border-primary/50 hover:bg-gray-200 dark:hover:bg-gray-800"
                      }`}
                      onClick={() => handleIndicatorSelect(indicator)}
                    >
                      <div className="flex gap-2">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 min-w-[1.5rem]">{index + 1}.</span>
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100">{indicator.indicatorName}</h4>
                          {department && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{department.departmentName}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            {frequency && (
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${frequencyColor}`}>
                                {frequency.frequencyName}
                              </span>
                            )}
                          </div>
                          
                          {/* Period Status Chips */}
                          {indicatorPeriodsData.length > 0 && (
                            <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                              <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-1.5 font-medium">Periods:</p>
                              <div className="flex flex-wrap gap-1">
                                {indicatorPeriodsData.slice(0, 5).map((period) => {
                                  const hasData = existingValues.some(
                                    v => v.indicatorId === indicator.id && v.periodId === period.id
                                  );
                                  return (
                                    <span
                                      key={period.id}
                                      className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                                        hasData
                                          ? "bg-green-500 text-white"
                                          : "bg-red-500 text-white"
                                      }`}
                                      title={`${period.periodLabel} - ${hasData ? 'Data filled' : 'No data'}`}
                                    >
                                      {period.periodLabel}
                                    </span>
                                  );
                                })}
                                {indicatorPeriodsData.length > 5 && (
                                  <span className="text-[9px] text-gray-500 dark:text-gray-400">
                                    +{indicatorPeriodsData.length - 5} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Data Entry Form */}
        <Card className="lg:col-span-2 border-2">
          <CardHeader className="py-3 border-b bg-gray-50 dark:bg-gray-800/50">
            {selectedIndicator ? (
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <FileText className="h-3.5 w-3.5 text-primary" />
                    <CardTitle className="text-sm font-semibold">
                      {existingValue ? "View Data" : "Enter Data"}
                    </CardTitle>
                  </div>
                  <CardDescription className="text-xs ml-5">
                    {selectedIndicator.indicatorName}
                  </CardDescription>
                </div>
                {departments.find(d => d.id === selectedIndicator.departmentId) && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                    {departments.find(d => d.id === selectedIndicator.departmentId)?.departmentName}
                  </span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <FileText className="h-3.5 w-3.5 text-gray-400" />
                <div>
                  <CardTitle className="text-sm font-semibold">Data Entry Form</CardTitle>
                  <CardDescription className="text-xs mt-0">
                    Select an indicator to begin
                  </CardDescription>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {!selectedIndicator ? (
              <div className="text-center py-12 text-gray-600 dark:text-gray-500">
                <FileText className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600 colorful:text-gray-500 yellow:text-[#fbb615]/50 pink:text-[#df1b82]/50 orange:text-[#f36f26]/50 blue:text-[#057dbb]/50 red:text-[#c22034]/50 green:text-[#3ead49]/50/50/50/50/50/50/50 mb-3" />
                <p className="text-gray-600 dark:text-gray-400">Please select an indicator to begin</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Period Selection */}
                <PeriodSelector
                  periods={periods}
                  selectedPeriodId={formData.periodId}
                  onPeriodChange={(periodId) => setFormData({ ...formData, periodId })}
                  existingValues={existingValues}
                  selectedIndicatorId={selectedIndicator?.id}
                />

                {/* Selected Period Display */}
                {formData.periodId && periods.length > 0 && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Selected Period: {periods.find(p => p.id === formData.periodId)?.periodLabel || 'Unknown'}
                      </span>
                    </div>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1 ml-6">
                      {(() => {
                        const period = periods.find(p => p.id === formData.periodId);
                        if (period) {
                          const start = new Date(period.periodStartDate).toLocaleDateString();
                          const end = new Date(period.periodEndDate).toLocaleDateString();
                          return `${start} - ${end}`;
                        }
                        return '';
                      })()}
                    </p>
                  </div>
                )}

                {/* Numerator (if applicable) */}
                {selectedIndicator.numeratorLabel && (
                  <div className="space-y-2">
                    <Label htmlFor="numerator">{selectedIndicator.numeratorLabel}</Label>
                    <Input
                      id="numerator"
                      type="number"
                      value={formData.numeratorValue || ""}
                      onChange={(e) => {
                        const numerator = e.target.value ? parseInt(e.target.value) : undefined;
                        const newFormData = { ...formData, numeratorValue: numerator };
                        
                        // Auto-calculate based on denominator presence
                        if (selectedIndicator.denominatorLabel) {
                          // If denominator label exists, calculate percentage when both are present
                          if (numerator !== undefined && formData.denominatorValue !== undefined && formData.denominatorValue !== 0) {
                            newFormData.value = parseFloat(((numerator / formData.denominatorValue) * 100).toFixed(2));
                          }
                        } else {
                          // If no denominator label, set value same as numerator
                          newFormData.value = numerator ?? 0;
                        }
                        
                        setFormData(newFormData);
                      }}
                      placeholder={`Enter ${selectedIndicator.numeratorLabel}`}
                      readOnly={!!existingValue}
                      className={existingValue ? "bg-gray-100 dark:bg-gray-800 cursor-not-allowed" : ""}
                    />
                  </div>
                )}

                {/* Denominator (if applicable) */}
                {selectedIndicator.denominatorLabel && (
                  <div className="space-y-2">
                    <Label htmlFor="denominator">{selectedIndicator.denominatorLabel}</Label>
                    <Input
                      id="denominator"
                      type="number"
                      value={formData.denominatorValue || ""}
                      onChange={(e) => {
                        const denominator = e.target.value ? parseInt(e.target.value) : undefined;
                        const newFormData = { ...formData, denominatorValue: denominator };
                        
                        // Auto-calculate based on numerator presence
                        if (selectedIndicator.numeratorLabel) {
                          // If numerator label exists, calculate percentage when both are present
                          if (formData.numeratorValue !== undefined && denominator !== undefined && denominator !== 0) {
                            newFormData.value = parseFloat(((formData.numeratorValue / denominator) * 100).toFixed(2));
                          }
                        } else {
                          // If no numerator label, set value same as denominator
                          newFormData.value = denominator ?? 0;
                        }
                        
                        setFormData(newFormData);
                      }}
                      placeholder={`Enter ${selectedIndicator.denominatorLabel}`}
                      readOnly={!!existingValue}
                      className={existingValue ? "bg-gray-100 dark:bg-gray-800 cursor-not-allowed" : ""}
                    />
                  </div>
                )}

                {/* Value */}
                <div className="space-y-2">
                  <Label htmlFor="value">Value * ({selectedIndicator.unit})</Label>
                  <Input
                    id="value"
                    type="number"
                    step="0.01"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                    placeholder="Enter value"
                    required
                      readOnly
                    className="bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
                  />
                </div>

                {/* Remarks - Collapsible */}
                {!existingValue && !showRemarks ? (
                  <div className="pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRemarks(true)}
                      className="text-xs"
                    >
                      <MessageSquare className="h-3 w-3 mr-2" />
                      Add Remarks (Optional)
                    </Button>
                  </div>
                ) : (showRemarks || existingValue) && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="remarks">Remarks {!existingValue && "(Optional)"}</Label>
                      {!existingValue && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowRemarks(false);
                            setFormData({ ...formData, remarks: "" });
                          }}
                          className="text-xs h-6 px-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    <Textarea
                      id="remarks"
                      value={formData.remarks}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, remarks: e.target.value })}
                      placeholder="Add any additional notes or remarks"
                      rows={3}
                      readOnly={!!existingValue}
                      className={existingValue ? "bg-gray-100 dark:bg-gray-800 cursor-not-allowed" : ""}
                    />
                  </div>
                )}

                {/* Submit Button or Read-only Info */}
                {existingValue ? (
                  <div className="pt-4">
                    <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-200">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span>Data already exists for this period. Viewing in read-only mode.</span>
                    </div>
                  </div>
                ) : (
                  <div className="pt-4">
                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      <Send className="h-4 w-4 mr-2" />
                      {isSubmitting ? "Submitting..." : "Submit Indicator Data"}
                    </Button>
                  </div>
                )}
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
